import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../../components/ui/command';
import { 
  ListChecks, Clock, CheckCircle, AlertTriangle, Calendar, Plus,
  Filter, Search, Loader2, ChevronRight, User, Users, UserPlus,
  Edit2, Trash2, LayoutGrid, List, CheckSquare, Square, X, GripVertical,
  CalendarDays, GanttChart, ChevronLeft,
  Globe, Briefcase, Handshake, MessageSquare, Copy
} from 'lucide-react';
import TaskComments, { CollaboratorsList } from '../../components/TaskComments';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const PRIORITY_COLORS = {
  'Low': 'bg-gray-100 text-gray-800',
  'Medium': 'bg-blue-100 text-blue-800',
  'High': 'bg-orange-100 text-orange-800',
  'Urgent': 'bg-red-100 text-red-800'
};

const PRIORITY_BORDER_COLORS = {
  'Low': 'border-l-gray-400',
  'Medium': 'border-l-blue-500',
  'High': 'border-l-orange-500',
  'Urgent': 'border-l-red-500'
};

const STATUS_COLORS = {
  'To Do': 'bg-slate-200 text-slate-700 border border-slate-300',
  'In Progress': 'bg-blue-100 text-blue-800 border border-blue-200',
  'Completed': 'bg-green-100 text-green-800 border border-green-200',
  'On Hold': 'bg-yellow-100 text-yellow-800 border border-yellow-200',
  'Cancelled': 'bg-red-100 text-red-800 border border-red-200'
};

const STATUS_DOT_COLORS = {
  'To Do': 'bg-slate-400',
  'Upcoming': 'bg-slate-400',
  'In Progress': 'bg-blue-500',
  'Completed': 'bg-green-500',
  'On Hold': 'bg-yellow-500',
  'Cancelled': 'bg-red-400'
};

const KANBAN_COLUMNS = ['Upcoming', 'In Progress', 'On Hold', 'Completed'];

const MyTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [viewMode, setViewMode] = useState('kanban'); // 'list' | 'kanban' | 'calendar' | 'timeline'
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [newChecklistAssignee, setNewChecklistAssignee] = useState('');
  const [newTaskChecklist, setNewTaskChecklist] = useState([]);
  const [newTaskChecklistInput, setNewTaskChecklistInput] = useState('');
  const [collabPopoverOpen, setCollabPopoverOpen] = useState(false);
  const [editingChecklistId, setEditingChecklistId] = useState(null);
  const [editingChecklistText, setEditingChecklistText] = useState('');
  const [draggedTask, setDraggedTask] = useState(null);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [collapsedColumns, setCollapsedColumns] = useState({});
  const [customColumns, setCustomColumns] = useState([]);
  const [showNewColumnInput, setShowNewColumnInput] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    task_type: 'self',
    assigned_to_id: '',
    collaborators: [],
    due_date: '',
    priority: 'Medium',
    category: ''
  });

  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'Medium',
    status: 'To Do'
  });

  const { user, getAuthHeaders, currentOffice } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }

      if (activeTab === 'all') {
        // Fetch all tasks from all endpoints and merge
        const [myTasksRes, createdRes, collabRes] = await Promise.all([
          fetch(`${API_URL}/api/tasks/my-tasks`, { headers }),
          fetch(`${API_URL}/api/tasks/created-by-me`, { headers }),
          fetch(`${API_URL}/api/tasks/collaborations`, { headers })
        ]);

        let allTasks = [];
        if (myTasksRes.ok) {
          const data = await myTasksRes.json();
          allTasks = [...allTasks, ...data];
        }
        if (createdRes.ok) {
          const data = await createdRes.json();
          allTasks = [...allTasks, ...data];
        }
        if (collabRes.ok) {
          const data = await collabRes.json();
          allTasks = [...allTasks, ...data];
        }

        // Deduplicate by id
        const uniqueTasks = allTasks.reduce((acc, task) => {
          if (!acc.find(t => t.id === task.id)) {
            acc.push(task);
          }
          return acc;
        }, []);

        setTasks(uniqueTasks);
      } else {
        let endpoint = `${API_URL}/api/tasks/my-tasks`;
        if (activeTab === 'created') {
          endpoint = `${API_URL}/api/tasks/created-by-me`;
        } else if (activeTab === 'collaboration') {
          endpoint = `${API_URL}/api/tasks/collaborations`;
        }

        const response = await fetch(endpoint, { headers });
        if (response.ok) {
          const data = await response.json();
          setTasks(data);
        }
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, activeTab, currentOffice?.id, getAuthHeaders]);

  useEffect(() => {
    fetchTasks();
    fetchEmployees();
  }, [activeTab, fetchTasks]);

  const fetchEmployees = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }
      
      const response = await fetch(`${API_URL}/api/employees/colleagues`, { headers });
      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }

      const payload = {
        ...formData,
        collaborators: formData.task_type === 'collaboration' ? formData.collaborators : []
      };

      const response = await fetch(`${API_URL}/api/tasks/`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const createdTask = await response.json();
        
        // Add checklist items if any
        if (newTaskChecklist.length > 0) {
          for (const item of newTaskChecklist) {
            await fetch(`${API_URL}/api/tasks/${createdTask.id}/checklist`, {
              method: 'POST', headers,
              body: JSON.stringify({ text: item }),
            }).catch(err => console.error('Failed to add checklist item:', err));
          }
        }
        
        setShowCreateModal(false);
        resetForm();
        fetchTasks();
        toast.success('Task created successfully');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create task');
      }
    } catch (error) {
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusUpdate = async (taskId, newStatus) => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
        if (selectedTask?.id === taskId) {
          setSelectedTask({ ...selectedTask, status: newStatus });
        }
        toast.success('Status updated');
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update status');
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates),
      });
      if (response.ok) {
        const updated = await response.json();
        setTasks(prev => prev.map(t => t.id === taskId ? updated : t));
        if (selectedTask?.id === taskId) setSelectedTask(updated);
        return true;
      } else {
        toast.error('Failed to update task');
        return false;
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Error updating task');
      return false;
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;

    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        fetchTasks();
        setShowDetailModal(false);
        toast.success('Task deleted');
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  // Checklist operations
  const handleAddChecklistItem = async () => {
    if (!newChecklistItem.trim() || !selectedTask) return;

    // Resolve assigned name from task members
    let assignedName = '';
    if (newChecklistAssignee) {
      if (selectedTask.assigned_to === newChecklistAssignee) {
        assignedName = selectedTask.assigned_to_name || '';
      } else {
        const collab = (selectedTask.collaborators || []).find(c => c.user_id === newChecklistAssignee);
        assignedName = collab?.name || '';
      }
    }

    try {
      const headers = getAuthHeaders();
      const body = { text: newChecklistItem.trim() };
      if (newChecklistAssignee) {
        body.assigned_to = newChecklistAssignee;
        body.assigned_name = assignedName;
      }
      const response = await fetch(`${API_URL}/api/tasks/${selectedTask.id}/checklist`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const updated = await response.json();
        setSelectedTask(updated);
        setTasks(prev => prev.map(t => t.id === selectedTask.id ? updated : t));
        setNewChecklistItem('');
        setNewChecklistAssignee('');
        toast.success('Checklist item added');
      }
    } catch (error) {
      console.error('Error adding checklist item:', error);
      toast.error('Failed to add item');
    }
  };

  const handleToggleChecklistItem = async (itemId) => {
    if (!selectedTask) return;

    const item = selectedTask.checklist?.find(i => i.id === itemId);
    if (!item) return;

    // Optimistic update
    const updatedChecklist = selectedTask.checklist.map(i =>
      i.id === itemId ? { ...i, completed: !i.completed } : i
    );
    setSelectedTask(prev => ({ ...prev, checklist: updatedChecklist }));

    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/tasks/${selectedTask.id}/checklist/${itemId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ completed: !item.completed })
      });

      if (response.ok) {
        const updated = await response.json();
        setSelectedTask(updated);
        setTasks(prev => prev.map(t => t.id === selectedTask.id ? updated : t));
      }
    } catch (error) {
      console.error('Error toggling checklist item:', error);
    }
  };

  const handleDeleteChecklistItem = async (itemId) => {
    if (!selectedTask) return;

    // Optimistic update
    const updatedChecklist = selectedTask.checklist.filter(i => i.id !== itemId);
    setSelectedTask(prev => ({ ...prev, checklist: updatedChecklist }));

    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/tasks/${selectedTask.id}/checklist/${itemId}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        const updated = await response.json();
        setSelectedTask(updated);
        setTasks(prev => prev.map(t => t.id === selectedTask.id ? updated : t));
      }
    } catch (error) {
      console.error('Error deleting checklist item:', error);
    }
  };

  // Mark My Part Done — completes all checklist items assigned to current user
  const handleMarkMyPartDone = async () => {
    if (!selectedTask) return;
    try {
      const response = await fetch(`${API_URL}/api/tasks/${selectedTask.id}/mark-my-part-done`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedTask(data.task);
        setTasks(prev => prev.map(t => t.id === selectedTask.id ? data.task : t));
        toast.success(`Completed ${data.items_completed} item(s)${data.all_done ? ' — task fully complete!' : `. ${data.remaining_count} remaining.`}`);
        fetchTasks();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to mark items done');
      }
    } catch (error) {
      toast.error('Failed to mark your part done');
    }
  };

  // Drag and drop handlers for Kanban
  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    if (!draggedTask || draggedTask.status === newStatus) {
      setDraggedTask(null);
      return;
    }

    await handleStatusUpdate(draggedTask.id, newStatus);
    setDraggedTask(null);
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
  };

  const openEditModal = (task) => {
    setEditingTask(task);
    setEditFormData({
      title: task.title || '',
      description: task.description || '',
      due_date: task.due_date || '',
      priority: task.priority || 'Medium',
      status: task.status || 'To Do'
    });
    setShowEditModal(true);
    setShowDetailModal(false);
  };

  const handleEditTask = async (e) => {
    e.preventDefault();
    if (!editingTask) return;
    
    setError('');
    setSubmitting(true);

    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/tasks/${editingTask.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(editFormData)
      });

      if (response.ok) {
        setShowEditModal(false);
        setEditingTask(null);
        fetchTasks();
        toast.success('Task updated');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update task');
      }
    } catch (error) {
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      task_type: 'self',
      assigned_to_id: '',
      collaborators: [],
      due_date: '',
      priority: 'Medium',
      category: ''
    });
    setNewTaskChecklist([]);
    setNewTaskChecklistInput('');
  };

  const handleDuplicateTask = (task) => {
    if (!task) return;
    setFormData({
      title: `${task.title} (Copy)`,
      description: task.description || '',
      task_type: task.task_type || 'self',
      assigned_to_id: task.assigned_to || '',
      collaborators: (task.collaborators || []).map(c => c.user_id || c.id).filter(Boolean),
      due_date: '', // due dates rarely make sense to copy verbatim
      priority: task.priority || 'Medium',
      category: task.category || '',
    });
    // Copy checklist items but reset their completion status
    setNewTaskChecklist(
      (task.checklist || []).map(item => ({
        title: item.title || item.text || '',
        assigned_to: item.assigned_to || null,
        assigned_to_name: item.assigned_to_name || null,
      }))
    );
    setNewTaskChecklistInput('');
    setShowDetailModal(false);
    setShowCreateModal(true);
    toast.info('Task copied — edit and save to create a duplicate');
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          task.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Map task status to kanban column
  const getKanbanStatus = (status) => {
    if (status === 'To Do') return 'Upcoming';
    return status;
  };

  // Map kanban column back to task status
  const getTaskStatus = (kanbanColumn) => {
    if (kanbanColumn === 'Upcoming') return 'To Do';
    return kanbanColumn;
  };

  // All columns including custom ones
  const allColumns = [...KANBAN_COLUMNS, ...customColumns];

  // Group tasks by status for Kanban (includes custom columns)
  const tasksByStatus = allColumns.reduce((acc, column) => {
    acc[column] = filteredTasks.filter(t => getKanbanStatus(t.status) === column);
    return acc;
  }, {});

  const stats = {
    total: tasks.length,
    pending: tasks.filter(t => !['Completed', 'Cancelled'].includes(t.status)).length,
    completed: tasks.filter(t => t.status === 'Completed').length,
    overdue: tasks.filter(t => t.is_overdue).length
  };

  const getTaskTypeIcon = (task) => {
    if (task.collaborators?.length > 0) return <Users className="h-4 w-4 text-purple-600" />;
    if (task.assigned_to_id && task.assigned_to_id !== task.created_by_id) return <UserPlus className="h-4 w-4 text-blue-600" />;
    return <User className="h-4 w-4 text-gray-600" />;
  };

  const getTaskTypeBadge = (task) => {
    if (task.collaborators?.length > 0) return <Badge className="bg-purple-100 text-purple-800 text-xs">Team</Badge>;
    if (task.assigned_to_id && task.assigned_to_id !== task.created_by_id) return <Badge className="bg-blue-100 text-blue-800 text-xs">Assigned</Badge>;
    return <Badge className="bg-gray-100 text-gray-800 text-xs">Personal</Badge>;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Kanban Task Card Component
  const KanbanTaskCard = ({ task }) => {
    const isDragging = draggedTask?.id === task.id;
    const completedItems = task.checklist?.filter(i => i.completed).length || 0;
    const totalItems = task.checklist?.length || 0;
    const borderColor = PRIORITY_BORDER_COLORS[task.priority] || 'border-l-gray-300';

    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: isDragging ? 0.6 : 1, y: 0, scale: isDragging ? 1.02 : 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.15 }}
        draggable
        onDragStart={(e) => handleDragStart(e, task)}
        onDragEnd={handleDragEnd}
        onClick={() => { setSelectedTask(task); setShowDetailModal(true); }}
        className={`p-3 bg-white dark:bg-gray-800 rounded-lg border-l-4 ${borderColor} border border-gray-200 dark:border-gray-700 shadow-sm cursor-grab active:cursor-grabbing
          hover:shadow-lg hover:border-gray-300 dark:hover:border-gray-600 transition-all group
          ${isDragging ? 'ring-2 ring-blue-500 shadow-xl z-50' : ''}`}
        style={{ touchAction: 'none' }}
        data-testid={`kanban-task-${task.id}`}
      >
        {/* Header with type and priority */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            {getTaskTypeIcon(task)}
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${PRIORITY_COLORS[task.priority]}`}>
              {task.priority}
            </span>
          </div>
          {task.is_overdue && (
            <Badge className="bg-red-100 text-red-800 text-[10px]">Overdue</Badge>
          )}
        </div>

        {/* Title */}
        <h4 className="font-medium text-sm mb-1 line-clamp-2">{task.title}</h4>

        {/* Description preview */}
        {task.description && (
          <p className="text-xs text-gray-500 mb-2 line-clamp-2">{task.description}</p>
        )}

        {/* Checklist progress */}
        {totalItems > 0 && (
          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <CheckSquare className="w-3 h-3" />
              <span>{completedItems}/{totalItems}</span>
            </div>
            <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-green-500 transition-all"
                style={{ width: `${totalItems > 0 ? (completedItems / totalItems) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}

        {/* Footer with due date and assignee */}
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          {task.due_date ? (
            <div className={`flex items-center gap-1 text-xs ${task.is_overdue ? 'text-red-500' : 'text-gray-500'}`}>
              <Calendar className="w-3 h-3" />
              <span>{formatDate(task.due_date)}</span>
            </div>
          ) : <div />}

          {task.assigned_to_name && (
            <Avatar className="h-5 w-5 border border-white">
              <AvatarFallback className="text-[9px] bg-blue-100 text-blue-700">
                {getInitials(task.assigned_to_name)}
              </AvatarFallback>
            </Avatar>
          )}
        </div>
      </motion.div>
    );
  };

  // Toggle column collapse
  const toggleColumnCollapse = (status) => {
    setCollapsedColumns(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
  };

  // Open create modal with specific status
  const openCreateWithStatus = (status) => {
    const taskStatus = getTaskStatus(status);
    setFormData(prev => ({
      ...prev,
      status: taskStatus
    }));
    setShowCreateModal(true);
  };

  // Add new custom column
  const handleAddColumn = () => {
    if (!newColumnName.trim()) return;
    const columnName = newColumnName.trim();
    if (!customColumns.includes(columnName) && !KANBAN_COLUMNS.includes(columnName)) {
      setCustomColumns(prev => [...prev, columnName]);
      // Also add to STATUS_DOT_COLORS dynamically
      STATUS_DOT_COLORS[columnName] = 'bg-purple-500';
    }
    setNewColumnName('');
    setShowNewColumnInput(false);
  };

  // Kanban Column Component
  const KanbanColumn = ({ status, tasks: columnTasks }) => {
    const isDropTarget = draggedTask && getKanbanStatus(draggedTask.status) !== status;
    const [isOver, setIsOver] = useState(false);
    const isCollapsed = collapsedColumns[status];

    const handleColumnDragOver = (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (!isOver) setIsOver(true);
    };

    const handleColumnDragLeave = (e) => {
      e.preventDefault();
      setIsOver(false);
    };

    const handleColumnDrop = async (e) => {
      e.preventDefault();
      setIsOver(false);
      if (!draggedTask || getKanbanStatus(draggedTask.status) === status) {
        setDraggedTask(null);
        return;
      }
      // Convert kanban column name back to task status
      const newStatus = getTaskStatus(status);
      await handleStatusUpdate(draggedTask.id, newStatus);
      setDraggedTask(null);
    };

    // Collapsed column view
    if (isCollapsed) {
      return (
        <div
          className={`w-12 rounded-xl p-2 transition-all duration-200 border-2 cursor-pointer
            ${isOver && isDropTarget 
              ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-400' 
              : 'bg-gray-50 dark:bg-gray-900 border-blue-200 dark:border-blue-800'}`}
          onDragOver={handleColumnDragOver}
          onDragLeave={handleColumnDragLeave}
          onDrop={handleColumnDrop}
          onClick={() => toggleColumnCollapse(status)}
          data-testid={`kanban-column-${status.replace(/\s/g, '-').toLowerCase()}-collapsed`}
        >
          <div className="flex flex-col items-center gap-2">
            <ChevronRight className="w-4 h-4 text-gray-500" />
            <span className={`w-2.5 h-2.5 rounded-full ${STATUS_DOT_COLORS[status] || STATUS_DOT_COLORS['To Do']}`} />
            <span className="text-xs font-medium text-gray-500 bg-white dark:bg-gray-800 px-1.5 py-0.5 rounded-full border border-gray-200 dark:border-gray-700">
              {columnTasks.length}
            </span>
            <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 writing-mode-vertical" 
                  style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
              {status}
            </span>
          </div>
        </div>
      );
    }

    return (
      <div
        className={`flex-1 min-w-[240px] max-w-[300px] rounded-xl p-3 transition-all duration-200 border-2
          ${isOver && isDropTarget 
            ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-400 scale-[1.02]' 
            : 'bg-gray-50 dark:bg-gray-900 border-blue-200 dark:border-blue-800'}`}
        onDragOver={handleColumnDragOver}
        onDragLeave={handleColumnDragLeave}
        onDrop={handleColumnDrop}
        data-testid={`kanban-column-${status.replace(/\s/g, '-').toLowerCase()}`}
      >
        {/* Column Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${STATUS_DOT_COLORS[status] || STATUS_DOT_COLORS['To Do']}`} />
            <h3 className="font-semibold text-sm">{status}</h3>
            <span className="text-xs text-gray-500 bg-white dark:bg-gray-800 px-1.5 py-0.5 rounded-full border border-gray-200 dark:border-gray-700">
              {columnTasks.length}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => openCreateWithStatus(status)}
              className="p-1 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded transition-colors text-blue-600"
              title="Add task to this column"
              data-testid={`add-task-${status.replace(/\s/g, '-').toLowerCase()}`}
            >
              <Plus className="w-4 h-4" />
            </button>
            <button
              onClick={() => toggleColumnCollapse(status)}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              title="Collapse column"
              data-testid={`collapse-column-${status.replace(/\s/g, '-').toLowerCase()}`}
            >
              <ChevronLeft className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Tasks */}
        <div className="space-y-2 min-h-[120px] max-h-[480px] overflow-y-auto">
          <AnimatePresence mode="popLayout">
            {columnTasks.map(task => (
              <KanbanTaskCard key={task.id} task={task} />
            ))}
          </AnimatePresence>

          {columnTasks.length === 0 && (
            <div className={`flex items-center justify-center h-[80px] text-gray-400 text-xs border-2 border-dashed rounded-lg transition-colors
              ${isOver && isDropTarget ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700'}`}>
              {isOver && isDropTarget ? 'Drop here' : 'No tasks'}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Calendar View Helper Functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    // Add empty cells for days before the first day of month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const getTasksForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return filteredTasks.filter(task => {
      if (!task.due_date) return false;
      const taskDate = task.due_date.split('T')[0];
      return taskDate === dateStr;
    });
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSameMonth = (date) => {
    if (!date) return false;
    return date.getMonth() === calendarDate.getMonth();
  };

  const handleCalendarDateClick = (date) => {
    if (!date) return;
    setSelectedCalendarDate(date);
    setFormData(prev => ({
      ...prev,
      due_date: date.toISOString().split('T')[0]
    }));
    setShowCreateModal(true);
  };

  const navigateMonth = (direction) => {
    setCalendarDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  // Calendar View Component
  const CalendarView = () => {
    const days = getDaysInMonth(calendarDate);
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <Card className="border-2 border-blue-200 dark:border-blue-800">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => navigateMonth(-1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <CardTitle className="text-lg">
              {monthNames[calendarDate.getMonth()]} {calendarDate.getFullYear()}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigateMonth(1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((date, index) => {
              const tasksForDay = getTasksForDate(date);
              const hasOverdue = tasksForDay.some(t => t.is_overdue);
              
              return (
                <div
                  key={index}
                  onClick={() => handleCalendarDateClick(date)}
                  className={`min-h-[100px] p-1 rounded-lg border transition-all cursor-pointer
                    ${!date ? 'bg-gray-50 dark:bg-gray-900 border-transparent' : 
                      isToday(date) ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-400' :
                      'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-blue-300 hover:bg-blue-50/50'}`}
                  data-testid={date ? `calendar-day-${date.getDate()}` : undefined}
                >
                  {date && (
                    <>
                      <div className={`text-sm font-medium mb-1 ${isToday(date) ? 'text-blue-600' : ''}`}>
                        {date.getDate()}
                      </div>
                      <div className="space-y-1">
                        {tasksForDay.slice(0, 3).map(task => (
                          <div
                            key={task.id}
                            onClick={(e) => { e.stopPropagation(); setSelectedTask(task); setShowDetailModal(true); }}
                            className={`text-[10px] px-1.5 py-0.5 rounded truncate cursor-pointer
                              ${task.status === 'Completed' ? 'bg-green-100 text-green-800 line-through' :
                                task.is_overdue ? 'bg-red-100 text-red-800' :
                                'bg-blue-100 text-blue-800'}`}
                          >
                            {task.title}
                          </div>
                        ))}
                        {tasksForDay.length > 3 && (
                          <div className="text-[10px] text-gray-500 px-1">
                            +{tasksForDay.length - 3} more
                          </div>
                        )}
                      </div>
                      {tasksForDay.length === 0 && (
                        <div className="flex items-center justify-center h-[60px] opacity-0 hover:opacity-100 transition-opacity">
                          <Plus className="w-4 h-4 text-gray-400" />
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Timeline View Component
  const TimelineView = () => {
    // Group tasks by date
    const tasksByDate = filteredTasks
      .filter(t => t.due_date)
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
      .reduce((acc, task) => {
        const dateKey = task.due_date.split('T')[0];
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(task);
        return acc;
      }, {});

    const tasksWithoutDate = filteredTasks.filter(t => !t.due_date);
    const sortedDates = Object.keys(tasksByDate).sort();

    const formatTimelineDate = (dateStr) => {
      const date = new Date(dateStr);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      if (date.toDateString() === today.toDateString()) return 'Today';
      if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
      
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      });
    };

    const isPastDate = (dateStr) => {
      const date = new Date(dateStr);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date < today;
    };

    return (
      <Card className="border-2 border-blue-200 dark:border-blue-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GanttChart className="w-5 h-5" />
            Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-blue-200 dark:bg-blue-800" />
            
            <div className="space-y-6">
              {sortedDates.map(dateStr => {
                const dayTasks = tasksByDate[dateStr];
                const isOverdue = isPastDate(dateStr) && dayTasks.some(t => t.status !== 'Completed');
                
                return (
                  <div key={dateStr} className="relative pl-10">
                    {/* Timeline dot */}
                    <div className={`absolute left-2.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-900
                      ${isOverdue ? 'bg-red-500' : 'bg-blue-500'}`} />
                    
                    {/* Date header */}
                    <div className={`text-sm font-semibold mb-2 ${isOverdue ? 'text-red-600' : 'text-gray-700 dark:text-gray-300'}`}>
                      {formatTimelineDate(dateStr)}
                      {isOverdue && <span className="ml-2 text-xs font-normal">(Overdue)</span>}
                    </div>
                    
                    {/* Tasks for this date */}
                    <div className="space-y-2">
                      {dayTasks.map(task => (
                        <div
                          key={task.id}
                          onClick={() => { setSelectedTask(task); setShowDetailModal(true); }}
                          className={`p-3 rounded-lg border-l-4 ${PRIORITY_BORDER_COLORS[task.priority]} bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                            cursor-pointer hover:shadow-md transition-all`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {task.status === 'Completed' ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              ) : (
                                <div className={`w-4 h-4 rounded-full border-2 ${STATUS_DOT_COLORS[task.status]?.replace('bg-', 'border-')}`} />
                              )}
                              <span className={`font-medium ${task.status === 'Completed' ? 'line-through text-gray-400' : ''}`}>
                                {task.title}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={PRIORITY_COLORS[task.priority]}>{task.priority}</Badge>
                              <Badge className={STATUS_COLORS[task.status]}>{task.status}</Badge>
                            </div>
                          </div>
                          {task.description && (
                            <p className="text-sm text-gray-500 mt-1 line-clamp-1">{task.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Tasks without due date */}
              {tasksWithoutDate.length > 0 && (
                <div className="relative pl-10">
                  <div className="absolute left-2.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-900 bg-gray-400" />
                  <div className="text-sm font-semibold mb-2 text-gray-500">No Due Date</div>
                  <div className="space-y-2">
                    {tasksWithoutDate.map(task => (
                      <div
                        key={task.id}
                        onClick={() => { setSelectedTask(task); setShowDetailModal(true); }}
                        className={`p-3 rounded-lg border-l-4 ${PRIORITY_BORDER_COLORS[task.priority]} bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                          cursor-pointer hover:shadow-md transition-all`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{task.title}</span>
                          <div className="flex items-center gap-2">
                            <Badge className={PRIORITY_COLORS[task.priority]}>{task.priority}</Badge>
                            <Badge className={STATUS_COLORS[task.status]}>{task.status}</Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {sortedDates.length === 0 && tasksWithoutDate.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <GanttChart className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No tasks to display in timeline</p>
                  <Button variant="outline" className="mt-4" onClick={() => setShowCreateModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Task
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-tasks-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Tasks</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage your tasks and collaborations</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} data-testid="create-task-button">
          <Plus className="h-4 w-4 mr-2" />
          Create Task
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('all')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">All Tasks</p>
                <p className="text-3xl font-bold">{stats.total}</p>
              </div>
              <ListChecks className="h-10 w-10 text-gray-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow bg-blue-50 dark:bg-blue-900/20 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700 dark:text-blue-400">In Progress</p>
                <p className="text-3xl font-bold text-blue-800 dark:text-blue-300">{stats.pending}</p>
              </div>
              <Clock className="h-10 w-10 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow bg-green-50 dark:bg-green-900/20 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700 dark:text-green-400">Completed</p>
                <p className="text-3xl font-bold text-green-800 dark:text-green-300">{stats.completed}</p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow bg-red-50 dark:bg-red-900/20 border-red-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-700 dark:text-red-400">Overdue</p>
                <p className="text-3xl font-bold text-red-800 dark:text-red-300">{stats.overdue}</p>
              </div>
              <AlertTriangle className="h-10 w-10 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Task Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-4">
          <TabsTrigger value="all" className="flex items-center gap-2" data-testid="tab-all">
            <ListChecks className="h-4 w-4" />
            All Tasks
          </TabsTrigger>
          <TabsTrigger value="assigned" className="flex items-center gap-2" data-testid="tab-assigned">
            <UserPlus className="h-4 w-4" />
            Assigned to Me
          </TabsTrigger>
          <TabsTrigger value="collaboration" className="flex items-center gap-2" data-testid="tab-collaboration">
            <Users className="h-4 w-4" />
            Collaborations
          </TabsTrigger>
          <TabsTrigger value="created" className="flex items-center gap-2" data-testid="tab-created">
            <User className="h-4 w-4" />
            Created by Me
          </TabsTrigger>
        </TabsList>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search tasks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="task-search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]" data-testid="status-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="To Do">To Do</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="On Hold">On Hold</SelectItem>
                </SelectContent>
              </Select>
              {/* View Toggle */}
              <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                <Button
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  data-testid="view-list"
                  title="List View"
                >
                  <List className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'kanban' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('kanban')}
                  data-testid="view-kanban"
                  title="Kanban View"
                >
                  <LayoutGrid className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'calendar' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('calendar')}
                  data-testid="view-calendar"
                  title="Calendar View"
                >
                  <CalendarDays className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'timeline' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('timeline')}
                  data-testid="view-timeline"
                  title="Timeline View"
                >
                  <GanttChart className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Task Content - List, Kanban, Calendar, or Timeline */}
        <TabsContent value={activeTab} className="mt-0">
          {viewMode === 'kanban' ? (
            /* Kanban View */
            <div className="flex gap-4 overflow-x-auto pb-4">
              {allColumns.map(status => (
                <KanbanColumn 
                  key={status} 
                  status={status} 
                  tasks={tasksByStatus[status] || []}
                />
              ))}
              
              {/* New List Button */}
              {showNewColumnInput ? (
                <div className="min-w-[240px] rounded-xl p-3 border-2 border-dashed border-blue-300 bg-blue-50/50 dark:bg-blue-900/20">
                  <div className="space-y-2">
                    <Input
                      value={newColumnName}
                      onChange={(e) => setNewColumnName(e.target.value)}
                      placeholder="Enter list name..."
                      className="text-sm"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddColumn();
                        if (e.key === 'Escape') { setShowNewColumnInput(false); setNewColumnName(''); }
                      }}
                      data-testid="new-column-input"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleAddColumn} disabled={!newColumnName.trim()}>
                        Add List
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setShowNewColumnInput(false); setNewColumnName(''); }}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowNewColumnInput(true)}
                  className="min-w-[200px] h-fit rounded-xl p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 
                    hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-all
                    flex items-center justify-center gap-2 text-gray-500 hover:text-blue-600"
                  data-testid="add-new-column"
                >
                  <Plus className="w-5 h-5" />
                  <span className="font-medium">New List</span>
                </button>
              )}
            </div>
          ) : viewMode === 'calendar' ? (
            /* Calendar View */
            <CalendarView />
          ) : viewMode === 'timeline' ? (
            /* Timeline View */
            <TimelineView />
          ) : (
            /* List View */
            <Card>
              <CardHeader>
                <CardTitle>
                  {activeTab === 'all' && 'All Tasks'}
                  {activeTab === 'assigned' && 'Tasks Assigned to Me'}
                  {activeTab === 'collaboration' && 'Collaboration Tasks'}
                  {activeTab === 'created' && 'Tasks I Created'}
                  {' '}({filteredTasks.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {filteredTasks.length > 0 ? (
                  <div className="space-y-3">
                    {filteredTasks.map(task => {
                      const completedItems = task.checklist?.filter(i => i.completed).length || 0;
                      const totalItems = task.checklist?.length || 0;
                      
                      return (
                        <div
                          key={task.id}
                          className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => { setSelectedTask(task); setShowDetailModal(true); }}
                          data-testid={`task-item-${task.id}`}
                        >
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`p-2 rounded-lg ${task.is_overdue ? 'bg-red-100' : 'bg-gray-100 dark:bg-gray-700'}`}>
                              {getTaskTypeIcon(task)}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-semibold">{task.title}</p>
                                {task.is_overdue && <Badge className="bg-red-100 text-red-800">Overdue</Badge>}
                                {getTaskTypeBadge(task)}
                                {/* Checklist indicator */}
                                {totalItems > 0 && (
                                  <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                                    <CheckSquare className="w-3 h-3" />
                                    {completedItems}/{totalItems}
                                  </span>
                                )}
                              </div>
                              {task.description && (
                                <p className="text-sm text-gray-500 line-clamp-1 mt-1">{task.description}</p>
                              )}
                              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                {task.due_date && (
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    Due: {new Date(task.due_date).toLocaleDateString()}
                                  </span>
                                )}
                                {task.created_by_name && activeTab !== 'created' && (
                                  <span className="flex items-center gap-1">
                                    <User className="h-3 w-3" />
                                    By: {task.created_by_name}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={PRIORITY_COLORS[task.priority]}>{task.priority}</Badge>
                            <Badge className={STATUS_COLORS[task.status]}>{task.status}</Badge>
                            <ChevronRight className="h-5 w-5 text-gray-400" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <ListChecks className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No tasks found</h3>
                    <p className="text-gray-600 mb-4">
                      {activeTab === 'all' && "No tasks available yet"}
                      {activeTab === 'assigned' && "No tasks have been assigned to you yet"}
                      {activeTab === 'collaboration' && "No collaboration tasks yet"}
                      {activeTab === 'created' && "You haven't created any tasks yet"}
                    </p>
                    <Button onClick={() => setShowCreateModal(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Task
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Task Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col p-0" data-testid="create-task-modal">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle>Create New Task</DialogTitle>
            <DialogDescription>Create a task for yourself, assign to someone, or collaborate</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTask} className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto px-6 py-2 space-y-4">
            {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

            <div className="space-y-2">
              <Label>Task Type</Label>
              <Select value={formData.task_type} onValueChange={(v) => setFormData({...formData, task_type: v, assigned_to_id: '', collaborators: []})}>
                <SelectTrigger data-testid="task-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      For Myself
                    </div>
                  </SelectItem>
                  <SelectItem value="assigned">
                    <div className="flex items-center gap-2">
                      <UserPlus className="h-4 w-4" />
                      Assign to Someone
                    </div>
                  </SelectItem>
                  <SelectItem value="collaboration">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Collaboration
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Collaborators - right below task type */}
            {formData.task_type === 'collaboration' && (
              <div className="space-y-2">
                <Label>Collaborators</Label>
                <Popover open={collabPopoverOpen} onOpenChange={setCollabPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="collaborators-select">
                      {formData.collaborators.length > 0 
                        ? `${formData.collaborators.length} selected` 
                        : 'Search and add collaborators...'}
                      <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search employees..." data-testid="collaborator-search-input" />
                      <CommandList>
                        <CommandEmpty>No employees found.</CommandEmpty>
                        <CommandGroup heading="Team Members">
                          {employees.filter(emp => !emp.is_cross_office).map(emp => (
                            <CommandItem key={emp.id} value={emp.name} onSelect={() => {
                              setFormData(prev => ({
                                ...prev,
                                collaborators: prev.collaborators.includes(emp.id)
                                  ? prev.collaborators.filter(c => c !== emp.id)
                                  : [...prev.collaborators, emp.id]
                              }));
                            }}>
                              <div className={`mr-2 h-4 w-4 border rounded flex items-center justify-center ${formData.collaborators.includes(emp.id) ? 'bg-blue-500 border-blue-500 text-white' : 'border-gray-300'}`}>
                                {formData.collaborators.includes(emp.id) && <CheckCircle className="h-3 w-3" />}
                              </div>
                              {emp.badge_type && (
                                <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 mr-1 ${emp.badge_type === 'service_provider' ? 'bg-emerald-500' : 'bg-violet-500'}`}>
                                  {emp.badge_type === 'service_provider' ? <Briefcase className="h-2 w-2 text-white" /> : <Handshake className="h-2 w-2 text-white" />}
                                </div>
                              )}
                              <span>{emp.name}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                        {employees.filter(emp => emp.is_cross_office).length > 0 && (
                          <CommandGroup heading="Cross-Office">
                            {employees.filter(emp => emp.is_cross_office).map(emp => (
                              <CommandItem key={emp.id} value={emp.name} onSelect={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  collaborators: prev.collaborators.includes(emp.id)
                                    ? prev.collaborators.filter(c => c !== emp.id)
                                    : [...prev.collaborators, emp.id]
                                }));
                              }}>
                                <div className={`mr-2 h-4 w-4 border rounded flex items-center justify-center ${formData.collaborators.includes(emp.id) ? 'bg-blue-500 border-blue-500 text-white' : 'border-gray-300'}`}>
                                  {formData.collaborators.includes(emp.id) && <CheckCircle className="h-3 w-3" />}
                                </div>
                                <span>{emp.name}</span>
                                <span className="text-xs text-muted-foreground ml-auto">{emp.office_name}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {formData.collaborators.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.collaborators.map(collabId => {
                      const emp = employees.find(e => e.id === collabId);
                      return (
                        <Badge key={collabId} variant="secondary" className="flex items-center gap-1">
                          {emp?.name}
                          <button type="button" onClick={() => setFormData({...formData, collaborators: formData.collaborators.filter(c => c !== collabId)})}
                            className="ml-1 hover:text-red-500">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Enter task title"
                required
                data-testid="task-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Add a description..."
                rows={3}
              />
            </div>

            {formData.task_type === 'assigned' && (
              <div className="space-y-2">
                <Label>Assign To *</Label>
                <Select value={formData.assigned_to_id} onValueChange={(v) => setFormData({...formData, assigned_to_id: v})}>
                  <SelectTrigger data-testid="assign-to-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.filter(emp => !emp.is_cross_office).map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        <div className="flex items-center gap-2">
                          {emp.badge_type && (
                            <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${emp.badge_type === 'service_provider' ? 'bg-emerald-500' : 'bg-violet-500'}`}>
                              {emp.badge_type === 'service_provider' ? <Briefcase className="h-2 w-2 text-white" /> : <Handshake className="h-2 w-2 text-white" />}
                            </div>
                          )}
                          <span>{emp.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                    {employees.filter(emp => emp.is_cross_office).length > 0 && (
                      <>
                        <div className="flex items-center gap-1.5 px-2 py-1.5 border-t">
                          <Globe className="h-3 w-3 text-emerald-600" />
                          <span className="text-xs font-medium text-emerald-700">Cross-Office</span>
                        </div>
                        {employees.filter(emp => emp.is_cross_office).map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>
                            <div className="flex items-center gap-2">
                              <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${emp.badge_type === 'service_provider' ? 'bg-emerald-500' : 'bg-violet-500'}`}>
                                {emp.badge_type === 'service_provider' ? <Briefcase className="h-2 w-2 text-white" /> : <Handshake className="h-2 w-2 text-white" />}
                              </div>
                              <span>{emp.name}</span>
                              <span className="text-xs text-muted-foreground ml-auto">{emp.office_name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                  data-testid="due-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                  <SelectTrigger data-testid="priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Checklist */}
            <div className="space-y-2">
              <Label>Checklist</Label>
              <div className="border rounded-lg p-3 space-y-2">
                {newTaskChecklist.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <CheckSquare className="h-4 w-4 text-blue-400 shrink-0" />
                    <span className="flex-1">{item}</span>
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-gray-400 hover:text-red-500"
                      onClick={() => setNewTaskChecklist(prev => prev.filter((_, i) => i !== idx))}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <Input value={newTaskChecklistInput}
                    onChange={e => setNewTaskChecklistInput(e.target.value)}
                    placeholder="Add checklist item..."
                    className="text-sm"
                    data-testid="new-task-checklist-input"
                    onKeyDown={e => {
                      if (e.key === 'Enter' && newTaskChecklistInput.trim()) {
                        e.preventDefault();
                        setNewTaskChecklist(prev => [...prev, newTaskChecklistInput.trim()]);
                        setNewTaskChecklistInput('');
                      }
                    }} />
                  <Button type="button" variant="outline" size="sm" disabled={!newTaskChecklistInput.trim()}
                    onClick={() => {
                      if (newTaskChecklistInput.trim()) {
                        setNewTaskChecklist(prev => [...prev, newTaskChecklistInput.trim()]);
                        setNewTaskChecklistInput('');
                      }
                    }} data-testid="add-checklist-item-btn">
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>

            </div>

            <DialogFooter className="px-6 py-3 border-t bg-background shrink-0">
              <Button type="button" variant="outline" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-task-button">
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                Create Task
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Task Detail Modal - Editable by default */}
      <Dialog open={showDetailModal} onOpenChange={(open) => {
        setShowDetailModal(open);
        if (!open) setSelectedTask(null);
      }}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="task-detail-modal">
          {selectedTask && (
            <form onSubmit={async (e) => {
              e.preventDefault();
              const success = await handleUpdateTask(selectedTask.id, {
                title: selectedTask.title,
                description: selectedTask.description,
                priority: selectedTask.priority,
                status: selectedTask.status,
                due_date: selectedTask.due_date
              });
              if (success) {
                toast.success('Task updated');
              }
            }}>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <Input
                    value={selectedTask.title}
                    onChange={(e) => setSelectedTask({...selectedTask, title: e.target.value})}
                    className="text-lg font-semibold border-0 border-b-2 border-transparent focus:border-blue-400 rounded-none px-0 focus-visible:ring-0"
                    placeholder="Task title..."
                    data-testid="edit-task-title-inline"
                  />
                  {selectedTask.is_overdue && <Badge className="bg-red-100 text-red-800 flex-shrink-0">Overdue</Badge>}
                </div>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                {/* Badges */}
                <div className="flex items-center gap-2 flex-wrap">
                  {getTaskTypeBadge(selectedTask)}
                </div>

                {/* Description - Editable */}
                <div>
                  <Label className="text-gray-500 text-xs">Description</Label>
                  <Textarea
                    value={selectedTask.description || ''}
                    onChange={(e) => setSelectedTask({...selectedTask, description: e.target.value})}
                    placeholder="Add a description..."
                    className="mt-1 min-h-[80px] resize-none"
                    rows={3}
                    data-testid="edit-task-description-inline"
                  />
                </div>

                {/* Status, Priority, Due Date Row */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-gray-500 text-xs">Status</Label>
                    <Select 
                      value={selectedTask.status} 
                      onValueChange={(v) => {
                        setSelectedTask({...selectedTask, status: v});
                        handleStatusUpdate(selectedTask.id, v);
                      }}
                    >
                      <SelectTrigger className="mt-1" data-testid="edit-status-inline">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="To Do">To Do</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                        <SelectItem value="On Hold">On Hold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Priority</Label>
                    <Select 
                      value={selectedTask.priority} 
                      onValueChange={(v) => setSelectedTask({...selectedTask, priority: v})}
                    >
                      <SelectTrigger className="mt-1" data-testid="edit-priority-inline">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Low">Low</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                        <SelectItem value="Urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Due Date</Label>
                    <Input
                      type="date"
                      value={selectedTask.due_date?.split('T')[0] || ''}
                      onChange={(e) => setSelectedTask({...selectedTask, due_date: e.target.value})}
                      className="mt-1"
                      data-testid="edit-duedate-inline"
                    />
                  </div>
                </div>

                {/* Checklist Section */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                      <CheckSquare className="w-4 h-4" />
                      Checklist
                      {selectedTask.checklist?.length > 0 && (
                        <span className="text-xs text-gray-500">
                          ({selectedTask.checklist.filter(i => i.completed).length}/{selectedTask.checklist.length})
                        </span>
                      )}
                    </Label>
                  </div>

                  {/* Checklist Progress Bar */}
                  {selectedTask.checklist?.length > 0 && (
                    <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden mb-3">
                      <div 
                        className="h-full bg-green-500 transition-all"
                        style={{ 
                          width: `${(selectedTask.checklist.filter(i => i.completed).length / selectedTask.checklist.length) * 100}%` 
                        }}
                      />
                    </div>
                  )}

                  {/* Checklist Items */}
                  <div className="space-y-2 mb-3">
                    {selectedTask.checklist?.map((item) => (
                      <div 
                        key={item.id} 
                        className={`flex items-center gap-3 group p-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 ${item.assigned_to === user?.id ? 'bg-blue-50/50 border border-blue-100' : ''}`}
                      >
                        <button
                          type="button"
                          onClick={() => handleToggleChecklistItem(item.id)}
                          className="flex-shrink-0"
                          data-testid={`checklist-toggle-${item.id}`}
                        >
                          {item.completed ? (
                            <CheckSquare className="w-5 h-5 text-green-500" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                        {editingChecklistId === item.id ? (
                          <Input
                            autoFocus
                            value={editingChecklistText}
                            onChange={e => setEditingChecklistText(e.target.value)}
                            className="flex-1 h-7 text-sm"
                            onKeyDown={async (e) => {
                              if (e.key === 'Enter' && editingChecklistText.trim()) {
                                const headers = getAuthHeaders();
                                await fetch(`${API_URL}/api/tasks/${selectedTask.id}/checklist/${item.id}`, {
                                  method: 'PUT', headers,
                                  body: JSON.stringify({ text: editingChecklistText.trim(), completed: item.completed }),
                                });
                                setSelectedTask(prev => ({
                                  ...prev,
                                  checklist: prev.checklist.map(c => c.id === item.id ? { ...c, text: editingChecklistText.trim() } : c)
                                }));
                                setEditingChecklistId(null);
                                fetchTasks();
                              } else if (e.key === 'Escape') {
                                setEditingChecklistId(null);
                              }
                            }}
                            onBlur={() => setEditingChecklistId(null)}
                          />
                        ) : (
                          <div className="flex-1 min-w-0">
                            <span
                              className={`text-sm cursor-pointer block ${item.completed ? 'line-through text-gray-400' : ''}`}
                              onDoubleClick={() => { setEditingChecklistId(item.id); setEditingChecklistText(item.text); }}
                              title="Double-click to edit"
                            >
                              {item.text}
                            </span>
                            {item.assigned_name && (
                              <span className={`text-[11px] flex items-center gap-1 mt-0.5 ${item.completed ? 'text-green-500' : 'text-blue-500'}`}>
                                <User className="w-3 h-3" />
                                {item.assigned_name}
                                {item.completed && item.completed_by_name && (
                                  <span className="text-green-500"> — done by {item.completed_by_name}</span>
                                )}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button type="button"
                            onClick={() => { setEditingChecklistId(item.id); setEditingChecklistText(item.text); }}
                            className="p-1 hover:bg-blue-50 rounded">
                            <Edit2 className="w-3.5 h-3.5 text-blue-500" />
                          </button>
                          <button type="button"
                            onClick={() => handleDeleteChecklistItem(item.id)}
                            className="p-1 hover:bg-red-50 rounded"
                            data-testid={`checklist-delete-${item.id}`}>
                            <X className="w-3.5 h-3.5 text-red-500" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Mark My Part Done Button */}
                  {selectedTask.checklist?.some(i => i.assigned_to === user?.id && !i.completed) && (
                    <div className="mb-3">
                      <Button
                        type="button"
                        onClick={handleMarkMyPartDone}
                        className="w-full bg-green-600 hover:bg-green-700 text-white gap-2"
                        data-testid="mark-my-part-done"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Mark My Part Done ({selectedTask.checklist.filter(i => i.assigned_to === user?.id && !i.completed).length} items)
                      </Button>
                    </div>
                  )}

                  {/* Add Checklist Item with Assignee */}
                  <div className="flex gap-2 items-center">
                    <Input
                      value={newChecklistItem}
                      onChange={(e) => setNewChecklistItem(e.target.value)}
                      placeholder="Add a sub-task..."
                      className="flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddChecklistItem();
                        }
                      }}
                      data-testid="new-checklist-item"
                    />
                    <Select
                      value={newChecklistAssignee || 'unassigned'}
                      onValueChange={(v) => setNewChecklistAssignee(v === 'unassigned' ? '' : v)}
                    >
                      <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="checklist-assignee-select">
                        <SelectValue placeholder="Assign to..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {selectedTask?.assigned_to && (
                          <SelectItem value={selectedTask.assigned_to}>
                            {selectedTask.assigned_to_name || 'Assignee'}
                          </SelectItem>
                        )}
                        {(selectedTask?.collaborators || []).map(c => (
                          <SelectItem key={c.user_id} value={c.user_id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      type="button"
                      variant="outline" 
                      size="icon"
                      onClick={handleAddChecklistItem}
                      disabled={!newChecklistItem.trim()}
                      data-testid="add-checklist-item"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Collaborators */}
                <div className="border-t pt-4">
                  <CollaboratorsList task={selectedTask} employees={employees} />
                  <div className="mt-2">
                    <Label className="text-gray-500 text-xs mb-1 block">Add Collaborator</Label>
                    <Select onValueChange={(v) => {
                      const currentCollabs = (selectedTask.collaborators || []).map(c => c.user_id);
                      if (!currentCollabs.includes(v)) {
                        handleUpdateTask(selectedTask.id, { collaborators: [...currentCollabs, v] });
                      }
                    }}>
                      <SelectTrigger className="h-9" data-testid="add-collaborator-select">
                        <SelectValue placeholder="Select team member..." />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.filter(emp => {
                          const existingIds = (selectedTask.collaborators || []).map(c => c.user_id);
                          return !existingIds.includes(emp.id) && emp.id !== selectedTask.assigned_to;
                        }).map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Comments */}
                <TaskComments
                  taskId={selectedTask.id}
                  task={selectedTask}
                  collaborators={selectedTask.collaborators || []}
                  employees={employees}
                />

                {/* Meta info */}
                {selectedTask.created_by_name && (
                  <div className="text-xs text-gray-500 pt-2 border-t">
                    Created by {selectedTask.created_by_name}
                    {selectedTask.created_at && ` on ${new Date(selectedTask.created_at).toLocaleDateString()}`}
                  </div>
                )}
              </div>

              <DialogFooter className="flex-wrap gap-2 mt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleDuplicateTask(selectedTask)}
                  data-testid="duplicate-task-button"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Duplicate
                </Button>
                <Button 
                  type="button"
                  variant="destructive" 
                  onClick={() => handleDeleteTask(selectedTask.id)} 
                  data-testid="delete-task-button"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowDetailModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-task-button">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Task Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col p-0" data-testid="edit-task-modal">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle>Edit Task</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditTask} className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto px-6 py-2 space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="edit-title">Title *</Label>
              <Input
                id="edit-title"
                value={editFormData.title}
                onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                placeholder="Task title"
                required
                data-testid="edit-task-title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                placeholder="Task description"
                rows={3}
                data-testid="edit-task-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-due-date">Due Date</Label>
                <Input
                  id="edit-due-date"
                  type="date"
                  value={editFormData.due_date}
                  onChange={(e) => setEditFormData({ ...editFormData, due_date: e.target.value })}
                  data-testid="edit-task-due-date"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-priority">Priority</Label>
                <Select value={editFormData.priority} onValueChange={(v) => setEditFormData({ ...editFormData, priority: v })}>
                  <SelectTrigger data-testid="edit-task-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-status">Status</Label>
              <Select value={editFormData.status} onValueChange={(v) => setEditFormData({ ...editFormData, status: v })}>
                <SelectTrigger data-testid="edit-task-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="To Do">To Do</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="On Hold">On Hold</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            </div>

            <DialogFooter className="px-6 py-3 border-t bg-background shrink-0">
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-edit-task-button">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyTasks;
